import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./PricingPage.css";

const API  = import.meta.env.VITE_API_URL ?? "http://localhost:5050";
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

// ── helpers ──────────────────────────────────────────────────────────────────
const uid   = () => Math.random().toString(36).slice(2, 9);
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (d: string, n: number) => {
  const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt.toISOString().slice(0, 10);
};

// ── types ────────────────────────────────────────────────────────────────────
interface Market   { code: string; label: string; currency: string; phone_prefixes: string[]; default: boolean; }
interface MealPlan { code: string; name: string; emoji: string; supplement_key: string | null; is_base: boolean; description: string; enabled: boolean; }
interface RateCell { double: number | ""; single?: number | ""; triple?: number | ""; quad?: number | ""; currency: string; }
interface Season   { _id: string; label: string; valid_from: string; valid_to: string; rates: Record<string, Record<string, RateCell>>; }
interface Override { id?: number; roomid: string; rate_date: string; rate: number; market_segment: string; occupancy_type: string; currency: string; }

interface Pricing {
  enabled: boolean;
  base_plan: string;
  markets: Market[];
  meal_plans: MealPlan[];
  seasons: Season[];
  supplements: Record<string, Record<string, number | null>>;
  children_policy: Record<string, {
    free_children: number; max_age: number;
    second_child_free_max_age?: number;
    second_child_half_max_age?: number;
    second_child_half_pct?: number;
  }>;
  family_room_supplement_pct: number;
  family_room_base_type: string;
}

const EMPTY: Pricing = {
  enabled: false, base_plan: "", markets: [], meal_plans: [], seasons: [],
  supplements: {}, children_policy: {}, family_room_supplement_pct: 50, family_room_base_type: "",
};

const TABS = ["Season Rates", "Meal Plans", "Supplements", "Children Policy", "Daily Overrides"] as const;

// ── detect season overlaps ───────────────────────────────────────────────────
function overlapError(seasons: Season[]): string | null {
  const sorted = [...seasons].sort((a, b) => a.valid_from.localeCompare(b.valid_from));
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].valid_to >= sorted[i + 1].valid_from)
      return `"${sorted[i].label}" overlaps with "${sorted[i + 1].label}"`;
  }
  return null;
}

// ── page ─────────────────────────────────────────────────────────────────────
export default function PricingPage() {
  const [tab, setTab]             = useState(0);
  const [pricing, setPricing]     = useState<Pricing>(EMPTY);
  const [roomTypes, setRoomTypes] = useState<string[]>([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [apiError, setApiError]   = useState<string | null>(null);
  const [apiOk, setApiOk]         = useState(false);

  // overrides state
  const [overrides, setOverrides]   = useState<Override[]>([]);
  const [ovrFrom, setOvrFrom]       = useState(today());
  const [ovrTo, setOvrTo]           = useState(addDays(today(), 30));
  const [ovrSel, setOvrSel]         = useState<Set<number | undefined>>(new Set());
  const [newOvr, setNewOvr]         = useState<Override>({
    roomid: "", rate_date: today(), rate: 0, market_segment: "", occupancy_type: "double", currency: "",
  });

  const upd = (patch: Partial<Pricing>) => setPricing(p => ({ ...p, ...patch }));

  // ── load ────────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/hotel/pricing`, auth());
      const p = { ...EMPTY, ...data.pricing } as Pricing;
      p.seasons    = (p.seasons    || []).map((s: any) => ({ _id: s._id || uid(), ...s }));
      p.meal_plans = (p.meal_plans || []).map((m: any) => ({ enabled: true, ...m }));
      setPricing(p);
      setRoomTypes(data.roomTypes || []);
      if (data.roomTypes?.[0]) setNewOvr(o => ({ ...o, roomid: data.roomTypes[0], currency: data.markets?.[0]?.currency || "" }));
    } catch (e: any) { setApiError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, []);

  const loadOverrides = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/hotel/pricing/overrides?from=${ovrFrom}&to=${ovrTo}`, auth());
      setOverrides(data.overrides || []);
    } catch (_) {}
  }, [ovrFrom, ovrTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 4) loadOverrides(); }, [tab, loadOverrides]);

  // ── save (called from each tab) ─────────────────────────────────────────────
  const save = async (overridePricing?: Pricing) => {
    const payload = overridePricing || pricing;
    setSaving(true); setApiError(null); setApiOk(false);
    try {
      const clean = { ...payload, seasons: payload.seasons.map(({ _id, ...s }) => s) };
      await axios.put(`${API}/api/hotel/pricing`, clean, auth());
      setPricing(payload);
      setApiOk(true);
      setTimeout(() => setApiOk(false), 3000);
    } catch (e: any) { setApiError(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="pr-loading">Loading pricing configuration…</div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="pr-page">

        {/* ── top bar ─────────────────────────────────────────────────────── */}
        <div className="pr-topbar">
          <div className="pr-topbar-left">
            <h1 className="pr-title">Pricing &amp; Packages</h1>
            <p className="pr-subtitle">Manage seasons, meal plans, supplements and daily rate overrides</p>
          </div>
          <div className="pr-master-wrap">
            <label className="pr-master-toggle">
              <span>Dynamic Pricing</span>
              <ToggleSwitch on={pricing.enabled} onChange={v => { const next = { ...pricing, enabled: v }; setPricing(next); save(next); }} />
            </label>
            <span className={`pr-master-hint ${pricing.enabled ? "on" : "off"}`}>
              {pricing.enabled
                ? "ON — the bot quotes guests using these season rates & supplements"
                : "OFF — the bot ignores this config and uses flat room rates only"}
            </span>
          </div>
        </div>

        {apiError && <div className="pr-alert error">{apiError}</div>}
        {apiOk    && <div className="pr-alert ok">Saved ✓</div>}

        {/* ── markets editor ─────────────────────────────────────────────── */}
        <MarketsEditor markets={pricing.markets} onChange={m => upd({ markets: m })} />

        {/* ── tabs ────────────────────────────────────────────────────────── */}
        <div className="pr-tabs">
          {TABS.map((t, i) => (
            <button key={t} className={`pr-tab${tab === i ? " active" : ""}`} onClick={() => setTab(i)}>
              {t}
            </button>
          ))}
        </div>

        {/* ── tab body ────────────────────────────────────────────────────── */}
        <div className="pr-body">
          {tab === 0 && (
            <SeasonRatesTab
              pricing={pricing} roomTypes={roomTypes}
              onChange={upd} saving={saving} onSave={() => save()}
            />
          )}
          {tab === 1 && (
            <MealPlansTab
              pricing={pricing} onChange={upd} saving={saving} onSave={() => save()}
            />
          )}
          {tab === 2 && (
            <SupplementsTab
              pricing={pricing} onChange={upd} saving={saving} onSave={() => save()}
            />
          )}
          {tab === 3 && (
            <ChildrenTab
              pricing={pricing} roomTypes={roomTypes}
              onChange={upd} saving={saving} onSave={() => save()}
            />
          )}
          {tab === 4 && (
            <OverridesTab
              overrides={overrides}
              from={ovrFrom} to={ovrTo}
              setFrom={setOvrFrom} setTo={setOvrTo}
              newOvr={newOvr} setNewOvr={setNewOvr}
              selected={ovrSel} setSelected={setOvrSel}
              markets={pricing.markets} roomTypes={roomTypes}
              onRefresh={loadOverrides}
              onAdd={async () => {
                await axios.post(`${API}/api/hotel/pricing/overrides`, [newOvr], auth());
                await loadOverrides();
              }}
              onDelete={async () => {
                const ids = [...ovrSel].filter(Boolean);
                if (!ids.length) return;
                await axios.delete(`${API}/api/hotel/pricing/overrides`, { ...auth(), data: { ids } });
                setOvrSel(new Set()); await loadOverrides();
              }}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ── Toggle ───────────────────────────────────────────────────────────────────
function ToggleSwitch({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button className={`pr-toggle ${on ? "on" : "off"}`} onClick={() => onChange(!on)}>
      <span className="pr-thumb" />
    </button>
  );
}

// ── Save bar (per-tab) ───────────────────────────────────────────────────────
function SaveBar({ saving, onSave, extra }: { saving: boolean; onSave: () => void; extra?: React.ReactNode }) {
  return (
    <div className="pr-savebar">
      {extra}
      <button className="pr-save-btn" disabled={saving} onClick={onSave}>
        {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}

// ── Markets editor ───────────────────────────────────────────────────────────
function MarketsEditor({ markets, onChange }: { markets: Market[]; onChange: (m: Market[]) => void }) {
  const [open, setOpen]     = useState(false);
  const [editing, setEditing] = useState<string | null>(null);  // market code being edited
  const [draft, setDraft]   = useState<Market>({ code: "", label: "", currency: "", phone_prefixes: [], default: false });

  const set = (k: keyof Market, v: any) => setDraft(d => ({ ...d, [k]: v }));

  const add = () => {
    if (!draft.code.trim() || !draft.label.trim() || !draft.currency.trim()) return;
    onChange([...markets, draft]);
    setDraft({ code: "", label: "", currency: "", phone_prefixes: [], default: false });
    setOpen(false);
  };

  const remove = (code: string) => onChange(markets.filter(m => m.code !== code));

  const setDefault = (code: string) =>
    onChange(markets.map(m => ({ ...m, default: m.code === code })));

  const updateMarket = (code: string, patch: Partial<Market>) =>
    onChange(markets.map(m => m.code === code ? { ...m, ...patch } : m));

  return (
    <div className="pr-mktbar">
      <span className="pr-mktbar-label">Markets</span>
      {markets.map(m => (
        <span key={m.code} className={`pr-mkt-chip ${m.default ? "default" : ""}`}>
          <span className="pr-mkt-curr">{m.currency}</span>
          <button className="pr-mkt-name pr-mkt-name-btn" title="Edit prefixes / details"
            onClick={() => setEditing(editing === m.code ? null : m.code)}>
            {m.label}
            <span className={`pr-mkt-prefixes ${m.phone_prefixes?.length ? "" : "warn"}`}>
              {m.phone_prefixes?.length ? `+${m.phone_prefixes.join(" +")}` : "no prefixes ⚠"}
            </span>
          </button>
          <button
            className={`pr-mkt-star ${m.default ? "on" : ""}`}
            title={m.default ? "Default market (guests with no matching prefix get this currency)" : "Set as default market"}
            onClick={() => setDefault(m.code)}
          >
            {m.default ? "★" : "☆"}
          </button>
          <button className="pr-mkt-rm" title="Remove market" onClick={() => remove(m.code)}>×</button>
        </span>
      ))}
      <button className="pr-mkt-add" onClick={() => { setOpen(v => !v); setEditing(null); }}>+ Add market</button>

      {/* Inline EDIT panel for an existing market */}
      {editing && (() => {
        const m = markets.find(x => x.code === editing);
        if (!m) return null;
        return (
          <div className="pr-mkt-form">
            <span className="pr-mkt-edit-label">Editing <b>{m.label}</b> ({m.code})</span>
            <input placeholder="Label" value={m.label}
              onChange={e => updateMarket(m.code, { label: e.target.value })} />
            <input placeholder="Currency" style={{ width: 80 }} value={m.currency}
              onChange={e => updateMarket(m.code, { currency: e.target.value })} />
            <input placeholder="Phone prefixes (comma, e.g. 20)" style={{ width: 200 }}
              value={(m.phone_prefixes || []).join(", ")}
              onChange={e => updateMarket(m.code, { phone_prefixes: e.target.value.split(",").map(x => x.trim().replace(/\D/g, "")).filter(Boolean) })} />
            <button className="pr-btn-ghost" onClick={() => setEditing(null)}>Done</button>
          </div>
        );
      })()}

      {/* ADD new market */}
      {open && (
        <div className="pr-mkt-form">
          <input placeholder="Code"     value={draft.code}     onChange={e => set("code", e.target.value)} />
          <input placeholder="Label"    value={draft.label}    onChange={e => set("label", e.target.value)} />
          <input placeholder="Currency" value={draft.currency} onChange={e => set("currency", e.target.value)} style={{ width: 80 }} />
          <input placeholder="Phone prefixes (comma)" onChange={e => set("phone_prefixes", e.target.value.split(",").map((x: string) => x.trim().replace(/\D/g, "")).filter(Boolean))} />
          <label><input type="checkbox" checked={draft.default} onChange={e => set("default", e.target.checked)} /> Default</label>
          <button className="pr-btn-gold" onClick={add}>Add</button>
          <button className="pr-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}

// ── Season Rates tab ─────────────────────────────────────────────────────────
function SeasonRatesTab({ pricing, roomTypes, onChange, saving, onSave }: {
  pricing: Pricing; roomTypes: string[];
  onChange: (p: Partial<Pricing>) => void;
  saving: boolean; onSave: () => void;
}) {
  const { seasons, markets } = pricing;
  const overlapErr = useMemo(() => overlapError(seasons), [seasons]);

  const addSeason = () => {
    const s: Season = { _id: uid(), label: "New Season", valid_from: today(), valid_to: addDays(today(), 30), rates: {} };
    onChange({ seasons: [...seasons, s] });
  };

  const dup = (i: number) => {
    const copy = { ...seasons[i], _id: uid(), label: seasons[i].label + " (copy)" };
    const next = [...seasons]; next.splice(i + 1, 0, copy);
    onChange({ seasons: next });
  };

  const del = (i: number) => onChange({ seasons: seasons.filter((_, j) => j !== i) });

  const updS = (i: number, patch: Partial<Season>) =>
    onChange({ seasons: seasons.map((s, j) => j === i ? { ...s, ...patch } : s) });

  const setRate = (si: number, rt: string, mc: string, field: "double" | "single" | "triple" | "quad", raw: string) => {
    const s = { ...seasons[si], rates: { ...seasons[si].rates } };
    const mkt = markets.find(m => m.code === mc);
    if (!s.rates[rt]) s.rates[rt] = {};
    const prev = s.rates[rt][mc] || { double: "", currency: mkt?.currency || "" };
    s.rates[rt][mc] = { ...prev, [field]: raw === "" ? "" : Number(raw) };
    onChange({ seasons: seasons.map((x, j) => j === si ? s : x) });
  };

  const noRooms   = !roomTypes.length;
  const noMarkets = !markets.length;

  return (
    <div className="pr-seasons">
      {overlapErr && <div className="pr-alert warn">⚠ Overlap: {overlapErr}</div>}
      {noRooms    && <div className="pr-alert warn">No room types detected — sync rooms first.</div>}
      {noMarkets  && <div className="pr-alert warn">Add at least one market above.</div>}

      {seasons.map((s, si) => {
        const dateErr = s.valid_from > s.valid_to;
        return (
          <div key={s._id} className="pr-season-card">
            {/* header row */}
            <div className="pr-shead">
              <input className="pr-sinput-label" value={s.label}
                onChange={e => updS(si, { label: e.target.value })} placeholder="Season name" />
              <div className="pr-sdates">
                <input type="date" value={s.valid_from} onChange={e => updS(si, { valid_from: e.target.value })} />
                <span className="pr-arrow">→</span>
                <input type="date" value={s.valid_to}   onChange={e => updS(si, { valid_to: e.target.value })} />
              </div>
              {dateErr && <span className="pr-date-err">from must be ≤ to</span>}
              <div className="pr-sbtns">
                <button className="pr-btn-ghost sm" onClick={() => dup(si)}>Duplicate</button>
                <button className="pr-btn-red   sm" onClick={() => del(si)}>Delete</button>
              </div>
            </div>

            {/* rate grid */}
            {!noRooms && !noMarkets && (
              <div className="pr-grid-scroll">
                <table className="pr-rgrid">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="pr-rt-hd">Room Type</th>
                      {markets.map(m => (
                        <th key={m.code} colSpan={4} className="pr-mkt-hd">{m.label}<span className="pr-curr"> {m.currency}</span></th>
                      ))}
                    </tr>
                    <tr>
                      {markets.map(m => (
                        <>
                          <th key={m.code + "d"} className="pr-occ-hd">Dbl</th>
                          <th key={m.code + "s"} className="pr-occ-hd">Sgl</th>
                          <th key={m.code + "t"} className="pr-occ-hd">Tpl</th>
                          <th key={m.code + "q"} className="pr-occ-hd">Quad</th>
                        </>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {roomTypes.map(rt => (
                      <tr key={rt}>
                        <td className="pr-rt-cell">{rt}</td>
                        {markets.map(m => {
                          const cell = s.rates[rt]?.[m.code];
                          return (
                            <>
                              <td key={m.code + "d"} className="pr-rate-td">
                                <input type="number" min="0" className="pr-rinput"
                                  value={cell?.double ?? ""}
                                  onChange={e => setRate(si, rt, m.code, "double", e.target.value)}
                                  placeholder="0" />
                              </td>
                              <td key={m.code + "s"} className="pr-rate-td">
                                <input type="number" min="0" className="pr-rinput"
                                  value={cell?.single ?? ""}
                                  onChange={e => setRate(si, rt, m.code, "single", e.target.value)}
                                  placeholder="—" />
                              </td>
                              <td key={m.code + "t"} className="pr-rate-td">
                                <input type="number" min="0" className="pr-rinput"
                                  value={cell?.triple ?? ""}
                                  onChange={e => setRate(si, rt, m.code, "triple", e.target.value)}
                                  placeholder="—" />
                              </td>
                              <td key={m.code + "q"} className="pr-rate-td">
                                <input type="number" min="0" className="pr-rinput"
                                  value={cell?.quad ?? ""}
                                  onChange={e => setRate(si, rt, m.code, "quad", e.target.value)}
                                  placeholder="—" />
                              </td>
                            </>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      <button className="pr-btn-dashed" onClick={addSeason}>+ Add season</button>
      <SaveBar saving={saving} onSave={onSave}
        extra={overlapErr ? <span className="pr-save-warn">⚠ Fix overlapping seasons before saving</span> : undefined}
      />
    </div>
  );
}

// ── Meal Plans tab ───────────────────────────────────────────────────────────
function MealPlansTab({ pricing, onChange, saving, onSave }: {
  pricing: Pricing; onChange: (p: Partial<Pricing>) => void; saving: boolean; onSave: () => void;
}) {
  const { meal_plans, markets, supplements } = pricing;

  // all supplement keys across all markets
  const suppKeys = useMemo(() => {
    const keys = new Set<string>();
    Object.values(supplements).forEach(m => Object.keys(m).forEach(k => keys.add(k)));
    if (!keys.size) ["bb_reduction", "hb_reduction", "hard_ai_supp"].forEach(k => keys.add(k));
    return [...keys];
  }, [supplements]);

  const updPlan = (i: number, patch: Partial<MealPlan>) => {
    const next = meal_plans.map((p, j) => j === i ? { ...p, ...patch } : p);
    if ("is_base" in patch && patch.is_base) {
      onChange({ meal_plans: next.map((p, j) => ({ ...p, is_base: j === i })), base_plan: next[i].code });
    } else {
      onChange({ meal_plans: next });
    }
  };

  const toggleEnabled = (i: number) => updPlan(i, { enabled: !meal_plans[i].enabled });

  const addPlan = () => onChange({
    meal_plans: [...meal_plans, {
      code: "PLAN" + uid().slice(0, 3).toUpperCase(), name: "New Plan",
      emoji: "🍽️", supplement_key: null, is_base: false, description: "", enabled: true,
    }],
  });

  const setSupp = (mCode: string, key: string, val: string) => {
    const cur = { ...(supplements[mCode] || {}) };
    cur[key] = val === "" ? null : Number(val);
    onChange({ supplements: { ...supplements, [mCode]: cur } });
  };

  return (
    <div className="pr-mealplans">
      <div className="pr-sh">Meal Plans</div>
      {meal_plans.map((mp, i) => (
        <div key={i} className={`pr-mp-row ${mp.is_base ? "base" : ""} ${!mp.enabled ? "disabled" : ""}`}>
          <ToggleSwitch on={mp.enabled} onChange={() => toggleEnabled(i)} />
          <input className="pr-mp-emoji" value={mp.emoji}        onChange={e => updPlan(i, { emoji: e.target.value })} />
          <input className="pr-mp-code"  value={mp.code}         onChange={e => updPlan(i, { code: e.target.value })}  placeholder="Code" />
          <input className="pr-mp-name"  value={mp.name}         onChange={e => updPlan(i, { name: e.target.value })}  placeholder="Name" />
          <input className="pr-mp-desc"  value={mp.description}  onChange={e => updPlan(i, { description: e.target.value })} placeholder="Short description" />
          <label className="pr-mp-base-lbl">
            <input type="radio" name="base_plan" checked={mp.is_base} onChange={() => updPlan(i, { is_base: true })} />
            Base plan
          </label>
          {!mp.is_base && (
            <select className="pr-select" value={mp.supplement_key ?? ""}
              onChange={e => updPlan(i, { supplement_key: e.target.value || null })}>
              <option value="">— supp key —</option>
              {suppKeys.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          )}
          <button className="pr-btn-icon-rm" onClick={() => onChange({ meal_plans: meal_plans.filter((_, j) => j !== i) })}>✕</button>
        </div>
      ))}
      <button className="pr-btn-dashed" onClick={addPlan}>+ Add meal plan</button>

      {markets.length > 0 && (
        <>
          <div className="pr-sh" style={{ marginTop: 32 }}>Supplement Amounts per Market</div>
          <div className="pr-supp-cols">
            {markets.map(m => (
              <div key={m.code} className="pr-supp-col">
                <div className="pr-supp-col-hd">{m.label} <span className="pr-curr">{m.currency}</span></div>
                <table className="pr-supp-tbl">
                  <tbody>
                    {suppKeys.map(k => (
                      <tr key={k}>
                        <td className="pr-sk">{k}</td>
                        <td><input type="number" className="pr-supp-in"
                          value={supplements[m.code]?.[k] ?? ""}
                          onChange={e => setSupp(m.code, k, e.target.value)}
                          placeholder="null" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </>
      )}

      <SaveBar saving={saving} onSave={onSave} />
    </div>
  );
}

// ── Supplements / Extras tab ─────────────────────────────────────────────────
const CORE_EXTRAS = ["extra_bed", "honeymoon_suite", "terrace_suite"];

function SupplementsTab({ pricing, onChange, saving, onSave }: {
  pricing: Pricing; onChange: (p: Partial<Pricing>) => void; saving: boolean; onSave: () => void;
}) {
  const { markets, supplements } = pricing;
  const [newKey, setNewKey] = useState("");

  const allKeys = useMemo(() => {
    const s = new Set([...CORE_EXTRAS]);
    Object.values(supplements).forEach(m => Object.keys(m).forEach(k => s.add(k)));
    return [...s];
  }, [supplements]);

  const setVal = (mc: string, key: string, val: string) => {
    const cur = { ...(supplements[mc] || {}) };
    cur[key] = val === "" ? null : Number(val);
    onChange({ supplements: { ...supplements, [mc]: cur } });
  };

  const addRow = () => {
    if (!newKey.trim()) return;
    const next = { ...supplements };
    markets.forEach(m => { next[m.code] = { ...(next[m.code] || {}), [newKey]: null }; });
    onChange({ supplements: next });
    setNewKey("");
  };

  if (!markets.length) return <div className="pr-empty">Add at least one market to configure supplements.</div>;

  return (
    <div className="pr-supplements">
      <div className="pr-sh">Extras &amp; Special Supplements</div>
      <div className="pr-grid-scroll">
        <table className="pr-supp-grid">
          <thead>
            <tr>
              <th>Supplement</th>
              {markets.map(m => <th key={m.code}>{m.label} <span className="pr-curr">({m.currency})</span></th>)}
            </tr>
          </thead>
          <tbody>
            {allKeys.map(key => (
              <tr key={key}>
                <td className="pr-sk">{key}</td>
                {markets.map(m => (
                  <td key={m.code}>
                    <input type="number" className="pr-supp-in"
                      value={supplements[m.code]?.[key] ?? ""}
                      onChange={e => setVal(m.code, key, e.target.value)}
                      placeholder="null" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pr-addrow">
        <input className="pr-input-sm" value={newKey} onChange={e => setNewKey(e.target.value)}
          onKeyDown={e => e.key === "Enter" && addRow()} placeholder="New supplement key…" />
        <button className="pr-btn-ghost sm" onClick={addRow}>Add row</button>
      </div>

      <div className="pr-sh" style={{ marginTop: 28 }}>Family Room</div>
      <div className="pr-fam-row">
        <label>Base room type
          <select className="pr-select" value={pricing.family_room_base_type}
            onChange={e => onChange({ family_room_base_type: e.target.value })}>
            <option value="">— none —</option>
            {Object.keys(pricing.children_policy).map(t => <option key={t}>{t}</option>)}
          </select>
        </label>
        <label>Supplement %
          <input type="number" min="0" max="200" className="pr-input-sm"
            value={pricing.family_room_supplement_pct}
            onChange={e => onChange({ family_room_supplement_pct: Number(e.target.value) })} />
        </label>
      </div>

      <SaveBar saving={saving} onSave={onSave} />
    </div>
  );
}

// ── Children Policy tab ──────────────────────────────────────────────────────
function ChildrenTab({ pricing, roomTypes, onChange, saving, onSave }: {
  pricing: Pricing; roomTypes: string[];
  onChange: (p: Partial<Pricing>) => void;
  saving: boolean; onSave: () => void;
}) {
  const { children_policy } = pricing;

  // Hotels store ages as "11.99" = "under 12". Show the clean whole number,
  // store back as N - 0.01 so the bot's `age <= max_age` math stays exact.
  const toDisplay = (stored?: number) =>
    stored == null || stored === "" as any ? "" : String(Math.ceil(stored));
  const toStore = (displayed: string) =>
    displayed === "" ? undefined : Math.max(0, Number(displayed) - 0.01);

  const upd = (rt: string, patch: object) =>
    onChange({ children_policy: { ...children_policy, [rt]: { ...(children_policy[rt] || { free_children: 1, max_age: 11.99 }), ...patch } } });

  if (!roomTypes.length) return <div className="pr-empty">No room types found. Sync rooms first.</div>;

  return (
    <div className="pr-children">
      <div className="pr-sh">Children Policy per Room Type
        <span className="pr-sh-sub">ages mean “under N years” — e.g. 12 = free up to a child's 12th birthday</span>
      </div>
      {roomTypes.map(rt => {
        const p = children_policy[rt] || { free_children: 1, max_age: 11.99 };
        return (
          <div key={rt} className="pr-child-card">
            <div className="pr-child-rt">{rt}</div>
            <div className="pr-child-grid">
              <label>Free children<input type="number" min="0" max="5"
                value={p.free_children ?? 1}
                onChange={e => upd(rt, { free_children: Number(e.target.value) })} /></label>
              <label>Free for kids under<span className="pr-yr-wrap"><input type="number" min="1" step="1"
                value={toDisplay(p.max_age)}
                onChange={e => upd(rt, { max_age: toStore(e.target.value) })} /><span className="pr-yr">yrs</span></span></label>
              <label>2nd child free under<span className="pr-yr-wrap"><input type="number" min="1" step="1"
                value={toDisplay(p.second_child_free_max_age)}
                onChange={e => upd(rt, { second_child_free_max_age: toStore(e.target.value) })}
                placeholder="—" /><span className="pr-yr">yrs</span></span></label>
              <label>2nd child half-price under<span className="pr-yr-wrap"><input type="number" min="1" step="1"
                value={toDisplay(p.second_child_half_max_age)}
                onChange={e => upd(rt, { second_child_half_max_age: toStore(e.target.value) })}
                placeholder="—" /><span className="pr-yr">yrs</span></span></label>
              <label>2nd child — half %<span className="pr-yr-wrap"><input type="number" min="0" max="100"
                value={p.second_child_half_pct ?? ""}
                onChange={e => upd(rt, { second_child_half_pct: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="—" /><span className="pr-yr">%</span></span></label>
            </div>
          </div>
        );
      })}
      <SaveBar saving={saving} onSave={onSave} />
    </div>
  );
}

// ── Daily Overrides tab ──────────────────────────────────────────────────────
function OverridesTab({ overrides, from, to, setFrom, setTo, newOvr, setNewOvr, selected, setSelected, markets, roomTypes, onRefresh, onAdd, onDelete }: {
  overrides: Override[]; from: string; to: string;
  setFrom: (s: string) => void; setTo: (s: string) => void;
  newOvr: Override; setNewOvr: React.Dispatch<React.SetStateAction<Override>>;
  selected: Set<number | undefined>; setSelected: (s: Set<number | undefined>) => void;
  markets: Market[]; roomTypes: string[];
  onRefresh: () => void; onAdd: () => void; onDelete: () => void;
}) {
  const setN = (k: keyof Override, v: any) => setNewOvr(o => ({ ...o, [k]: v }));
  const mktCurrency = (code: string) => markets.find(m => m.code === code)?.currency || "";

  return (
    <div className="pr-overrides">
      <div className="pr-sh">Daily Rate Overrides <span className="pr-sh-sub">Eid · Holidays · Special events</span></div>

      {/* date range filter */}
      <div className="pr-ovr-filter">
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <span>→</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        <button className="pr-btn-ghost sm" onClick={onRefresh}>Refresh</button>
      </div>

      {/* add form */}
      <div className="pr-ovr-form">
        <div className="pr-ovr-form-row">
          <select value={newOvr.roomid} onChange={e => setN("roomid", e.target.value)}>
            <option value="">Room type…</option>
            {roomTypes.map(rt => <option key={rt} value={rt}>{rt}</option>)}
          </select>
          <input type="date" value={newOvr.rate_date} onChange={e => setN("rate_date", e.target.value)} />
          <select value={newOvr.market_segment}
            onChange={e => { setN("market_segment", e.target.value); setN("currency", mktCurrency(e.target.value)); }}>
            <option value="">Market…</option>
            {markets.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
          </select>
          <select value={newOvr.occupancy_type} onChange={e => setN("occupancy_type", e.target.value)}>
            <option value="double">Double</option>
            <option value="single">Single</option>
            <option value="triple">Triple</option>
            <option value="quad">Quad</option>
          </select>
          <div className="pr-ovr-rate">
            <input type="number" min="0" value={newOvr.rate || ""}
              onChange={e => setN("rate", Number(e.target.value))} placeholder="Rate" />
            <span className="pr-curr">{newOvr.currency}</span>
          </div>
          <button className="pr-btn-gold" onClick={onAdd}
            disabled={!newOvr.roomid || !newOvr.market_segment || !newOvr.rate}>
            Add Override
          </button>
        </div>
      </div>

      {/* table */}
      {selected.size > 0 && (
        <div className="pr-ovr-bulk">
          <span>{selected.size} selected</span>
          <button className="pr-btn-red sm" onClick={onDelete}>Delete selected</button>
        </div>
      )}

      <div className="pr-grid-scroll">
        <table className="pr-ovr-tbl">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox"
                  checked={overrides.length > 0 && overrides.every(o => selected.has(o.id))}
                  onChange={e => setSelected(e.target.checked ? new Set(overrides.map(o => o.id)) : new Set())} />
              </th>
              <th>Room</th><th>Date</th><th>Market</th><th>Occupancy</th><th>Rate</th><th>Currency</th>
            </tr>
          </thead>
          <tbody>
            {overrides.map(o => (
              <tr key={o.id} className={selected.has(o.id) ? "sel" : ""}>
                <td><input type="checkbox" checked={selected.has(o.id)}
                  onChange={e => {
                    const s = new Set(selected);
                    e.target.checked ? s.add(o.id) : s.delete(o.id);
                    setSelected(s);
                  }} /></td>
                <td>{o.roomid}</td>
                <td>{o.rate_date}</td>
                <td>{o.market_segment}</td>
                <td>{o.occupancy_type}</td>
                <td className="pr-rate-num">{o.rate}</td>
                <td>{o.currency}</td>
              </tr>
            ))}
            {!overrides.length && (
              <tr><td colSpan={7} className="pr-empty-row">No overrides in this date range</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
